/*
 * Reviews Widget — API-driven Customer Reviews block, scoped to [data-reviews-widget].
 * Mirrors review-drawer.js fetch ergonomics (AbortController 8s, URLSearchParams, scope-to-root,
 * error-region writes). A single lazy fetch drives both the header summary and the widget.
 * Hooks: [data-reviews-widget] (root), [data-reviews-summary] (cross-root sibling, queried once),
 *        [data-reviews-bottomline-*], [data-reviews-distribution]/[data-reviews-dist-row],
 *        [data-reviews-gauge-fill], [data-reviews-dist-count], [data-reviews-list],
 *        [data-reviews-load-more], [data-reviews-announce], [data-reviews-empty], [data-reviews-error],
 *        [data-star-glyph].
 * All UGC rendered via textContent only; every field presence-guarded.
 */

;(function () {
  const API_HOST = 'https://api-cdn.yotpo.com';
  const PER_PAGE = 10;
  const TIMEOUT_MS = 8000;

  class ReviewsWidget {
    constructor(root) {
      this.root = root;
      this.appkey = root.dataset.reviewsAppkey;
      this.productId = root.dataset.reviewsProductId;
      this.errGeneric = root.dataset.reviewsErrorGeneric || '';
      // header summary lives OUTSIDE this root (in the title column) — query from document ONCE for the
      //   summary root only, then scope sub-queries to it. (Allowed: the single sanctioned cross-root query.)
      this.summary = document.querySelector('[data-reviews-summary]');
      this.bottomline = root.querySelector('[data-reviews-bottomline]');
      this.list = root.querySelector('[data-reviews-list]');
      this.loadMoreBtn = root.querySelector('[data-reviews-load-more]');
      this.announce = root.querySelector('[data-reviews-announce]');
      this.emptyEl = root.querySelector('[data-reviews-empty]');
      this.errorEl = root.querySelector('[data-reviews-error]');
      this.distRows = [...root.querySelectorAll('[data-reviews-dist-row]')];
      this.wordOne = root.dataset.reviewsWordOne || 'review';
      this.wordOther = root.dataset.reviewsWordOther || 'reviews';
      this.page = 0;
      this.total = 0;
      this.loaded = 0;
      this.busy = false;
      if (!this.appkey || !this.productId) return; // no config → leave server-rendered empties
      this.observe();
      if (this.loadMoreBtn) this.loadMoreBtn.addEventListener('click', () => this.fetchPage(this.page + 1));
      // SNAG 2 — header summary is an operable control; clicking it scrolls to the widget.
      //   Native <button> handles Enter/Space natively, so no manual key handler is needed.
      if (this.summary) this.summary.addEventListener('click', () => this.scrollToWidget());
    }

    scrollToWidget() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      // move focus to the widget for keyboard/AT users (root carries tabindex="-1")
      this.root.focus({ preventScroll: true });
    }

    observe() {
      // Eager path: the header summary ([data-reviews-summary]) is meant to populate on first
      // paint without requiring a scroll — but it ships `hidden` (display:none) in server markup,
      // and a display:none element has no box to intersect, so an IntersectionObserver watching
      // it never reports it as intersecting even though it sits in the initial viewport. Fetch
      // immediately whenever a header summary is present instead of waiting on that observer.
      if (this.summary) {
        if (this.announce) this.announce.textContent = this.root.dataset.reviewsLoading || '';
        this.fetchPage(1);
        return;
      }
      // Lazy path: no header summary on this PDP layout — defer the fetch until the widget
      // itself nears the viewport, keeping it out of the LCP critical path.
      const io = new IntersectionObserver((entries, obs) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        obs.disconnect();
        if (this.announce) this.announce.textContent = this.root.dataset.reviewsLoading || '';
        this.fetchPage(1);
      }, { rootMargin: '200px' });
      io.observe(this.root);
    }

    buildUrl(page) {
      // page=1 is the API default, so omit it — its cached aggregate can lag behind the
      // no-page one on Yotpo's CDN, hiding a just-published review. Only send page from 2 on.
      const params = new URLSearchParams({ per_page: String(PER_PAGE) });
      if (page > 1) params.set('page', String(page));
      return `${API_HOST}/v1/widget/${encodeURIComponent(this.appkey)}/products/${encodeURIComponent(this.productId)}/reviews.json?${params}`;
    }

    fetchPage(page) {
      if (this.busy) return;
      this.busy = true;
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), TIMEOUT_MS);
      if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('aria-disabled', 'true');
      fetch(this.buildUrl(page), { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error('http'); return r.json(); })
        .then((data) => this.render(data, page))
        .catch(() => this.showError())
        .finally(() => {
          clearTimeout(t);
          this.busy = false;
          if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('aria-disabled');
        });
    }

    render(data, page) {
      const resp = data && data.response ? data.response : {};
      const bottomline = resp.bottomline || {};
      const pagination = resp.pagination || {};
      const reviews = Array.isArray(resp.reviews) ? resp.reviews : [];
      this.page = pagination.page || page;
      // total is authoritative on page 1 (incl. a genuine 0 → empty state); on later pages only
      //   overwrite when the response actually carries a non-zero total, so a missing pagination.total
      //   on page 2+ cannot collapse a known total to 0 and prematurely hide LOAD MORE.
      const t = pagination.total || bottomline.total_review || 0;
      if (page === 1 || t) this.total = t;

      if (page === 1) {
        this.renderBottomline(bottomline);
        this.renderDistribution(bottomline);
        // The aggregate can lag the review payload on Yotpo's CDN (see buildUrl), so a zero total
        // alone must not hide reviews the same response carries.
        if (this.total === 0 && reviews.length === 0) { this.showEmpty(); return; }
        this.revealAggregate();
      }
      this.appendCards(reviews);
      this.loaded += reviews.length;
      this.toggleLoadMore();
      this.announce.textContent = ''; // clear loading
    }

    renderBottomline(b) {
      const avg = Number(b.average_score || 0);
      const count = Number(b.total_review || 0);
      const scoreText = avg ? avg.toFixed(1) : '';
      const word = count === 1 ? this.wordOne : this.wordOther;
      // widget bottomline
      setText(this.root, '[data-reviews-bottomline-score]', scoreText);
      setText(this.root, '[data-reviews-bottomline-count]', count ? ` (${count} ${word})` : '');
      this.fillStars(this.root.querySelector('[data-reviews-bottomline-stars]'), Math.round(avg));
      // header summary (shared) — score + bare (count), no word
      if (this.summary) {
        setText(this.summary, '[data-reviews-summary-score]', scoreText);
        setText(this.summary, '[data-reviews-summary-count]', count ? ` (${count})` : '');
        this.fillStars(this.summary.querySelector('[data-reviews-summary-stars]'), Math.round(avg));
      }
    }

    fillStars(container, n) {
      if (!container) return;
      const glyphs = [...container.querySelectorAll('[data-star-glyph]')];
      glyphs.forEach((g, i) => {
        g.classList.toggle('text-primary', i < n);
        g.classList.toggle('text-rule-tertiary', i >= n);
      });
    }

    renderDistribution(b) {
      const dist = b.star_distribution || {};
      const total = Number(b.total_review || 0) || 1;
      this.distRows.forEach((row) => {
        const star = row.dataset.star;
        const c = Number(dist[star] || 0);
        const fill = row.querySelector('[data-reviews-gauge-fill]');
        if (fill) fill.style.width = `${Math.round((c / total) * 100)}%`;
        setText(row, '[data-reviews-dist-count]', `(${c})`);
      });
    }

    appendCards(reviews) {
      reviews.forEach((rev) => this.list.appendChild(this.buildCard(rev)));
    }

    buildCard(rev) {
      // ALL text via textContent. Presence-guard every field.
      const score = Math.round(Number(rev && rev.score) || 0);

      // The divider sits further from the date/author row than the row sits from the body, so it
      // carries the difference as its own top margin.
      const card = el('div', 'flex flex-col gap-2xl w-full');

      const content = el('div', 'flex flex-col gap-md w-full md:w-5/6 lg:w-full lg:pr-2xl xl:pr-5xl');

      // stars (filled = score)
      const stars = el('span', 'flex items-center gap-2xs text-rule-tertiary');
      stars.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 5; i += 1) {
        const glyph = el('span', i < score ? 'inline-flex text-primary' : 'inline-flex text-rule-tertiary');
        glyph.appendChild(starSvg());
        stars.appendChild(glyph);
      }
      content.appendChild(stars);

      // title (skip the node if blank)
      const title = rev && typeof rev.title === 'string' ? rev.title.trim() : '';
      if (title) {
        const titleEl = el('p', 'd4 text-primary');
        titleEl.textContent = title;
        content.appendChild(titleEl);
      }

      // body (skip the node if blank)
      const body = rev && typeof rev.content === 'string' ? rev.content.trim() : '';
      if (body) {
        const bodyEl = el('p', 'b2 text-primary');
        bodyEl.textContent = body;
        content.appendChild(bodyEl);
      }

      card.appendChild(content);

      // meta row: date + vertical divider + name
      const meta = el('div', 'flex items-start gap-md');

      const dateText = this.formatDate(rev && rev.created_at);
      if (dateText) {
        const dateEl = el('span', 'b2 text-tertiary');
        dateEl.textContent = dateText;
        meta.appendChild(dateEl);
      }

      const name = rev && rev.user && typeof rev.user.display_name === 'string'
        ? rev.user.display_name.trim()
        : '';
      if (name) {
        if (dateText) meta.appendChild(el('span', 'w-px bg-rule-tertiary self-stretch'));
        const nameEl = el('span', 'b2 text-primary');
        nameEl.textContent = name;
        meta.appendChild(nameEl);
      }

      if (meta.childNodes.length) card.appendChild(meta);

      // card hairline divider
      card.appendChild(el('div', 'h-px bg-rule-tertiary w-full mt-xs lg:mt-md'));

      return card;
    }

    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d)) return '';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}/${d.getFullYear()}`;
    }

    toggleLoadMore() {
      const more = this.loaded < this.total;
      this.loadMoreBtn.classList.toggle('hidden', !more);
    }

    revealAggregate() {
      // D46 #1 / TECH-DEBT #85: a product with no reviews shows neither the header summary nor
      // the widget bottomline — no 0.0 score, no (0) count, no grey placeholder stars.
      // Both surfaces ship `.hidden` in the server markup and are revealed here ONLY once the
      // page-1 fetch confirms total > 0. This is the inverse of the old hide-when-empty path:
      // an empty colourway never flashes stars, so a full-page colour navigation to a 0-review
      // sibling product no longer shows-then-collapses the aggregate row (the "flicker/jolt").
      // Colourways WITH reviews reveal in place after the fetch resolves.
      if (this.summary) this.summary.classList.remove('hidden');
      if (this.bottomline) this.bottomline.classList.remove('hidden');
    }

    showEmpty() {
      this.emptyEl.classList.remove('hidden');
      // An empty list is still an in-flow flex sibling, so it would emit a column gap below the hairline.
      this.list.classList.add('hidden');
      this.announce.textContent = '';
    }

    showError() {
      // Unhide before writing: a display:none role="alert" is outside the accessibility tree, so text
      // set while hidden is never announced.
      if (this.errorEl) { this.errorEl.classList.remove('hidden'); this.errorEl.textContent = this.errGeneric; }
      // An empty list is still an in-flow flex sibling, so it would emit a column gap below the error.
      this.list.classList.add('hidden');
      this.announce.textContent = '';
      this.loadMoreBtn.classList.add('hidden');
    }
  }

  function el(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function starSvg() {
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.setAttribute('viewBox', '0 0 12 12');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', 'M6 0L7.63161 4.27215L12 4.58359L8.64 7.53537L9.7082 12L6 9.55215L2.2918 12L3.36 7.53537L0 4.58359L4.36839 4.27215L6 0Z');
    path.setAttribute('fill', 'currentColor');
    svg.appendChild(path);
    return svg;
  }

  function setText(scope, sel, text) { const el = scope.querySelector(sel); if (el) el.textContent = text; }

  function init(scope) {
    scope.querySelectorAll('[data-reviews-widget]').forEach((el) => {
      if (el.hasAttribute('data-reviews-bound')) return;
      el.setAttribute('data-reviews-bound', '');
      new ReviewsWidget(el);
    });
  }
  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
